import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Users } from 'lucide-react';
import { PipelineCandidateCard } from './PipelineCandidateCard';
import type { PipelineEntry, PipelineColumnConfig } from '../../types/pipeline';
import { PIPELINE_COLUMN_HEADER_STYLES } from '../../constants/pipelineColumns';

interface PipelineColumnProps {
  column: PipelineColumnConfig;
  entries: PipelineEntry[];
  missionId: number;
}

/**
 * PipelineColumn — droppable column for PipelineKanbanView.
 * Uses useDroppable so cards from other columns can be dropped here.
 * SortableContext provides inter-column movement (intra-column order not persisted in v1).
 */
export const PipelineColumn: React.FC<PipelineColumnProps> = ({ column, entries, missionId }) => {
  const { setNodeRef, isOver } = useDroppable({ id: column.slug });

  const headerStyle = PIPELINE_COLUMN_HEADER_STYLES[column.slug] ?? {
    bg: 'bg-slate-100',
    text: 'text-slate-700',
  };

  return (
    <div className="flex-1 flex flex-col min-w-[280px] max-w-[320px]">
      {/* Column Header */}
      <div className={`flex items-center justify-between px-4 py-3 rounded-t-xl ${headerStyle.bg} mb-2 shadow-sm`}>
        <span className={`font-bold text-sm tracking-wide ${headerStyle.text}`}>
          {column.label}
        </span>
        <span className="bg-white/70 px-2 py-0.5 rounded-full text-xs font-bold text-slate-600 shadow-sm border border-slate-200/50">
          {entries.length}
        </span>
      </div>

      {/* Column Body — Droppable Area */}
      <div
        ref={setNodeRef}
        className={`
          flex-1 rounded-b-xl rounded-tr-xl p-3 space-y-3 overflow-y-auto min-h-[200px]
          transition-colors duration-150
          ${isOver ? 'bg-slate-200/70' : 'bg-slate-100/50'}
        `}
      >
        <SortableContext
          items={entries.map((e) => String(e.matchId))}
          strategy={verticalListSortingStrategy}
        >
          {entries.map((entry) => (
            <PipelineCandidateCard
              key={entry.matchId}
              entry={entry}
              missionId={missionId}
            />
          ))}
        </SortableContext>

        {/* Empty state — UI-SPEC copywriting */}
        {entries.length === 0 && !isOver && (
          <div className="flex flex-col items-center justify-center py-8 text-slate-300 select-none pointer-events-none">
            <Users size={28} className="mb-2" />
            <p className="text-xs font-medium text-center">Glissez un candidat ici</p>
          </div>
        )}
      </div>
    </div>
  );
};
